import React, { useState, useContext, useEffect, useCallback } from 'react';
import { I18nContext } from '@bingads-webui-react/i18n-context';
import { ClientEntitySelector, ItemType } from '@bingads-webui-fluent-component/entity-selector';
import { makeStyles } from '@fluentui/react-components';
import { ScopeContext } from '@bingads-webui-react/scope-service';

import { fetchTopics, searchFlatItems } from './data';

export interface ITopic {
  TopicId: number;
  Id: number | null;
}
export interface ITreeTopic {
  value: string; // Topic ID
  content: string; // Topic name
  parentValue?: string | null; // Parent topic ID
}

export interface ITopicsSelector {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  selectedTopics: ITopic[];
  setSelectedTopics: (selectedTopics: ITopic[]) => void;
  isTopicsTargetingEnabled: boolean;
}

const useStyles = makeStyles({
  container: {
    '& .fui-Input__contentAfter': {
      wordWrap: 'normal',
    },
  },
});

export const TopicsSelector = ({
  open, onOpenChange, selectedTopics, setSelectedTopics, isTopicsTargetingEnabled,
}: ITopicsSelector) => {
  const i18n: any = useContext(I18nContext);
  const { scope } = useContext(ScopeContext) || {};
  const classes = useStyles();
  const [selected, setSelected] = useState<ItemType[]>([]);
  const [data, setData] = useState<ITreeTopic[]>([]);

  const { aid, cid } = scope.values;

  useEffect(() => {
    const fetchData = async () => {
      const { allTopics, fetchedSelectedTopics } = await fetchTopics(selectedTopics, aid, cid);
      setData(allTopics);
      setSelected(fetchedSelectedTopics);
    };

    isTopicsTargetingEnabled && fetchData();
  }, [isTopicsTargetingEnabled, selectedTopics, aid, cid]);

  const handleOnChange = (value: any[]) => {
    const selectedIds = value.map(v => Number(v.value));
    const initialSelectedIds = selectedTopics.map(topic => topic.TopicId);
    const transformedIdsToTopics: ITopic[] = selectedIds.map((id) => {
      if (!initialSelectedIds.includes(id)) {
        return { TopicId: id, Id: null };
      }
      return selectedTopics.find(topic => topic.TopicId === id) || { TopicId: id, Id: null };
    });

    setSelected(value);
    setSelectedTopics(transformedIdsToTopics);
  };

  const onRenderTagContent = useCallback((item) => {
    const topicTag = i18n.getString(_TL_('{{topic}}'), { topic: item.content });
    return (<div aria-label={topicTag}>{topicTag}</div>);
  }, [i18n]);

  return (
    <>
      <ClientEntitySelector
        i18n={i18n}
        className={classes.container}
        titleText={i18n.getString(_TL_('Select topics'))}
        open={open}
        onOpenChange={onOpenChange}
        data={data}
        searchboxProps={{
          placeholder: i18n.getString(_TL_('Search or browse topics')),
        }}
        searchFn={searchFlatItems}
        selectionMode="multiselect"
        tagViewLimit={5}
        value={selected}
        onChange={(value: any[]) => handleOnChange(value)}
        onRenderTagContent={onRenderTagContent}
        popOverContentBefore={i18n.getString(_TL_('Topics ({{topicsLength}})'), { topicsLength: data.length })}
      />
    </>
  );
};
